import { useState, type ReactNode } from 'react'
import { Form, Modal, type FormInstance } from 'antd'
import { DEFAULT_CLIENT_TEXT_COLOR } from '@/features/client/clientFormShared'

type ReportFormValues = {
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

type Colors = {
  primary: string
  secondary: string
  text: string
}

const DEFAULT_PRIMARY = '#1E40AF'
const DEFAULT_SECONDARY = '#64748B'
const HAIRLINE = '#DCE0E5'

// Match the deck's editorial type system: Caslon serif display + Archivo body.
const SERIF = "'Libre Caslon Text', Georgia, 'Times New Roman', Times, serif"
const SANS = "'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif"

// 16:9 video frame.
const FRAME_W = 960
const FRAME_H = 540

const FALLBACK_CLIENT_NAME = 'Demo Test Client'
const SAMPLE_PERIOD = 'JUL 2024 – JUN 2025'
const SAMPLE_TITLE = 'Executive Summary'
const SAMPLE_BULLETS: string[] = [
  'Revenue grew 8.3% to $4.24M',
  'Net profit rose 14% to $487K',
  'Gross margin expanded to 41.5%',
  'Cash runway at 5.2 months needs attention',
  'Services now near half of total income',
]

// Placeholder standing in for the narrator avatar the video composites over the slide —
// a head-and-shoulders cutout anchored to the bottom-right corner.
function AvatarPlaceholder({ colors }: { colors: Colors }) {
  return (
    <svg
      width={214}
      height={250}
      viewBox="0 0 214 250"
      style={{ position: 'absolute', right: 78, bottom: 0, display: 'block' }}
      aria-hidden
    >
      {/* head */}
      <circle cx={107} cy={74} r={56} fill={colors.secondary} />
      {/* shoulders / torso rising from the bottom edge */}
      <path d="M 6 250 L 6 205 Q 6 150 107 138 Q 208 150 208 205 L 208 250 Z" fill={colors.secondary} />
    </svg>
  )
}

function VideoFrame({ colors, clientName }: { colors: Colors; clientName: string }) {
  return (
    <div
      style={{
        width: FRAME_W,
        height: FRAME_H,
        background: '#fff',
        fontFamily: SANS,
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-bleed top accent bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: 8, background: colors.primary }} />

      {/* Header: client name (left) + advisory period (right) over a hairline */}
      <div
        style={{
          position: 'absolute',
          left: 70,
          top: 30,
          width: 820,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 16,
        }}
      >
        <span style={{ fontFamily: SERIF, fontSize: 23, fontWeight: 700, color: colors.primary, whiteSpace: 'nowrap' }}>
          {clientName}
        </span>
        <span
          style={{
            fontFamily: SANS,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: colors.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          Advisory · {SAMPLE_PERIOD}
        </span>
      </div>
      <div style={{ position: 'absolute', left: 70, top: 64, width: 820, height: 1, background: HAIRLINE }} />

      {/* Title + underline accent */}
      <div style={{ position: 'absolute', left: 70, top: 84, width: 820 }}>
        <div style={{ fontFamily: SERIF, fontSize: 54, fontWeight: 700, lineHeight: 1, color: colors.primary }}>
          {SAMPLE_TITLE}
        </div>
        <div style={{ width: 72, height: 5, background: colors.secondary, marginTop: 14 }} />
      </div>

      {/* Bullets (kept clear of the avatar on the right) */}
      <div style={{ position: 'absolute', left: 70, top: 210, right: 340 }}>
        {SAMPLE_BULLETS.map((b, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: 20,
              color: colors.text,
              fontSize: 22,
              lineHeight: 1.4,
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: colors.secondary,
                marginTop: 11,
                marginRight: 18,
              }}
            />
            <span>{b}</span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 70, right: 70, top: 476, height: 1, background: HAIRLINE }} />
      <div
        style={{
          position: 'absolute',
          left: 70,
          top: 492,
          fontFamily: SANS,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: colors.primary,
          opacity: 0.4,
        }}
      >
        Prepared using Dashboard Insights
      </div>

      <AvatarPlaceholder colors={colors} />
    </div>
  )
}

function ScaledFrame({ width, children }: { width: number; children: ReactNode }) {
  const scale = width / FRAME_W
  const height = FRAME_H * scale
  return (
    <div
      style={{
        width,
        height,
        overflow: 'hidden',
        background: '#000',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
        border: '1px solid #E5E7EB',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: FRAME_W,
          height: FRAME_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export function VideoReportPreview({
  form,
  clientName,
}: {
  form: FormInstance<ReportFormValues>
  clientName?: string
}) {
  const [open, setOpen] = useState(false)
  const primary = (Form.useWatch('primaryColor', form) || '').trim() || DEFAULT_PRIMARY
  const secondary = (Form.useWatch('secondaryColor', form) || '').trim() || DEFAULT_SECONDARY
  const text = (Form.useWatch('textColor', form) || '').trim() || DEFAULT_CLIENT_TEXT_COLOR
  const colors: Colors = { primary, secondary, text }
  const resolvedClientName = clientName?.trim() || FALLBACK_CLIENT_NAME

  return (
    <div className="mt-6">
      <div className="text-sm font-medium text-gray-700 mb-2">Video preview</div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex p-3 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left w-full"
        aria-label="Open enlarged video preview"
      >
        <ScaledFrame width={400}>
          <VideoFrame colors={colors} clientName={resolvedClientName} />
        </ScaledFrame>
      </button>
      <div className="text-xs text-gray-500 mt-1.5">Click to enlarge · Representative frame from the avatar summary video</div>

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={1140} title="Video preview" centered destroyOnClose>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <ScaledFrame width={1080}>
            <VideoFrame colors={colors} clientName={resolvedClientName} />
          </ScaledFrame>
        </div>
      </Modal>
    </div>
  )
}
